import { Box, Paper, Typography, IconButton, Divider } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { selectDrone } from '../../store/slices/dronesSlice';
import { droneLabel } from '../../types/drone';
import { useNow } from '../../hooks/useNow';
import { isTelemetryStale } from '../../utils/staleness';
import { DroneImage } from './DroneImage';
import { BatteryStatus } from './BatteryStatus';
import { LandingThresholdControl } from './LandingThresholdControl';
import { FlightControls } from './FlightControls';
import { DroneNameEditor } from './DroneNameEditor';
import { DroneStatusChips } from './DroneStatusChips';
import {
  formatLatLng,
  formatAltitude,
  formatSpeed,
  formatHeading,
  formatRelativeTime,
} from '../../utils/formatters';
import { droneService } from '../../services/droneService';

export const DroneDetailsPanel: React.FC = () => {
  const dispatch = useDispatch();
  const selectedDroneId = useSelector(
    (state: RootState) => state.drones.selectedDroneId
  );
  const drone = useSelector((state: RootState) =>
    selectedDroneId ? state.drones.drones[selectedDroneId] : null
  );
  // Above the early return, because a hook cannot live after a conditional.
  // Ticks so a drone that stops reporting while this panel is open becomes
  // stale on screen, rather than on the next click (#577).
  const now = useNow();

  const handleClose = () => {
    dispatch(selectDrone(null));
  };

  if (!drone) {
    return null;
  }

  const stale = isTelemetryStale(drone.lastUpdateTime, now);

  return (
    <Paper
      sx={{
        width: '100%',
        flexGrow: 1,
        overflow: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
      elevation={3}
    >
      {/* Header: the name, and the control that renames it (#406, G9). The
          full report card is on the Fleet page; this panel is for flying. */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        {/* Keyed by drone: a rename in progress belongs to the drone it was
            started on, and must not survive a click on another marker, where
            Enter would rename the wrong aircraft (#774). */}
        <DroneNameEditor key={drone.id} drone={drone} />
        <IconButton onClick={handleClose} size="small" aria-label="Close drone details">
          <Close />
        </IconButton>
      </Box>

      {/* Drone Image */}
      <DroneImage
        imageUrl={
          drone.imageUrl ? droneService.getDroneImageUrl(drone.id) : null
        }
        droneName={droneLabel(drone)}
      />

      {/* Status, and who owns this aircraft (#385). */}
      <DroneStatusChips drone={drone} />

      <Divider />

      {/* Basic Info */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            ID
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
            {drone.id}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Coordinates
          </Typography>
          <Typography variant="body2">
            {/* "Never reported" is the honest reading of a missing position, and
                a more useful one than a formatted 0°, 0° (#508). */}
            {drone.position
              ? formatLatLng(drone.position.latitude, drone.position.longitude)
              : 'Never reported'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Altitude
          </Typography>
          <Typography variant="body2">
            {drone.position ? formatAltitude(drone.position.altitude) : '—'}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Speed
          </Typography>
          <Typography variant="body2">{formatSpeed(drone.speed)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Heading
          </Typography>
          <Typography variant="body2">{formatHeading(drone.heading)}</Typography>
        </Box>
      </Box>

      <Divider />

      {/* Flight controls (#578). Above the battery rather than buried at the
          bottom: the reason an operator opens this panel during a flight is to
          do something about the aircraft. */}
      <FlightControls drone={drone} />

      <Divider />

      {/* Battery Status */}
      <BatteryStatus
        percentage={drone.battery.percentage}
        voltage={drone.battery.voltage}
        isHealthy={drone.battery.isHealthy}
        landingPolicy={drone.landingPolicy}
      />

      {/* Where the aircraft's own limit is set, and where it is changed (#629).
          Below the reading rather than beside it: reading the battery is what an
          operator does every time they open this panel, and setting the threshold
          is what they do once per airframe. */}
      <LandingThresholdControl drone={drone} />

      <Divider />

      {/* Last Update.

          For a drone that has gone quiet, this is promoted from a footnote to
          a statement about everything above it (#577). A 61-day-old position
          and state were rendered exactly like a live drone's, with the age
          available only here, in caption grey, at the bottom of the panel. */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        {stale ? (
          <Typography variant="body2" color="warning.main" role="status">
            Last seen {formatRelativeTime(drone.lastUpdateTime)}. Position,
            state and battery above are from that report, not from now.
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Last updated: {formatRelativeTime(drone.lastUpdateTime)}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
